import LoggerService from "@bfx/logger";
import { LogMessageTimeType } from "shared/broker-client-services/worker/CubiqLogger.constants";
const logger = LoggerService.for(__dirname);

/**
 * Contains all configuration flags for Cubiq logging.
 *
 * One instance can be shared between all logger instances
 */
export class CubiqLoggerConfig {
  /** type of time logging */
  public logMessageTimeType: LogMessageTimeType = LogMessageTimeType.NONE;

  /** Time the last log message was printed - used when MAX_INTERVAL logging is user */
  public lastLogTime: number = 0;

  /** Should message Id be shown? If so then the size of the Id is goverend by the showShortMsgId flag */
  public showMessageId: boolean = true;

  /** Out only the initial 8 chars of msg ID? */
  public showShortMsgId: boolean = true;

  public warnUnsupportedResponseMember: boolean = true;

  /** Should error args be displayed - as these may contain arbitrary data, it's best to leave this turned off */
  public showErrorArgs: boolean = false;

  /** Msg ID prefix logged out on next log() call */
  public currentMessageId: string = "";

  /** Enable/disable different message types - keyed on the encdingType */
  private requestMap: Map<string, boolean> = new Map();

  /** Enable/disable different response logging based on request types - keyed on the encdingType */
  private responseMap: Map<string, boolean> = new Map();

  /** Enable/disable different request types - keyed on the encdingType */
  private messageMap: Map<string, boolean> = new Map();

  constructor() {
    this.initialize();
  }

  private initialize() {
    this.requestMap.set("SecureTokenRefreshRequestImpl", true);
    this.requestMap.set("PingTimerImpl", true);
    this.responseMap.set("SecureTokenRefreshRequestImpl", true);
    this.responseMap.set("PingTimerImpl", true);
  }

  /**
   * Configure with a simple object of properties whose names match private config member properties
   * Not really useful at the moment - easiest to change this class - but could be used to change config at runtime
   * e.g. {showShortMsgId:false, showErrorArgs:true }
   * @param config
   */
  public configure(config: { [key: string]: any }) {
    for (const key in config) {
      if (key in this) {
        const oldValue = this[key];
        const newValue = config[key];
        if (typeof oldValue === typeof newValue) {
          this[key] = newValue;
        } else {
          logger.out.warn(
            `cub-m - Types of old[${typeof oldValue}] and new[${typeof newValue}] are different for ${key} - ignoring`,
          );
        }
      } else {
        logger.out.warn(`cub-m - cannot find config property ${key} - ignoring`);
      }
    }
  }

  /**
   * Configure request logging with a simple object of - requestType=> boolean properties
   * @param config
   */
  public configureRequest(config: { [key: string]: boolean }) {
    for (const key in config) {
      const value = config[key];
      this.requestMap.set(key, value);
    }
  }
  /**
   * Configure request logging with a simple object of - requestType=> boolean properties (we can't necessarily
   * tell the responseType from the requestType)
   * @param config
   */
  public configureResponse(config: { [key: string]: boolean }) {
    for (const key in config) {
      const value = config[key];
      this.responseMap.set(key, value);
    }
  }
  /**
   * Configure request logging with a simple object of - messagetype=> boolean properties
   * @param config
   */
  public configureMessage(config: { [key: string]: boolean }) {
    for (const key in config) {
      const value = config[key];
      this.messageMap.set(key, value);
    }
  }

  /**
   * Should the specified requesrt type be logged?
   * @param requestType
   * @returns
   */
  public outputRequestType(requestType: string): boolean {
    if (!this.requestMap.has(requestType)) {
      // Default value for all request types is true
      this.requestMap.set(requestType, true);
    }
    return this.requestMap.get(requestType);
  }

  /**
   * should the specified message type be logged?
   * @param messageType
   * @returns
   */
  public outputMessageType(messageType: string): boolean {
    if (!this.messageMap.has(messageType)) {
      // Default value for all message types is true
      this.messageMap.set(messageType, true);
    }
    return this.messageMap.get(messageType);
  }

  /**
   * Should the response assocaited with the specified request type be logged?
   * @param requestType
   * @returns
   */
  public outputResponseType(requestType: string): boolean {
    if (!this.responseMap.has(requestType)) {
      // Default value for all request types is true
      this.responseMap.set(requestType, true);
    }
    return this.responseMap.get(requestType);
  }
}
